import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/core/firebase';
import type { Trainer } from '@/types';
import { resolveTrainerContactPhone, TRAINER_CONTACT_DEFAULTS } from '@/lib/contacts';

export type TrainerDetailsSource = 'real' | 'ai' | 'none';

export interface TrainerDetails {
  id?: string;
  source: TrainerDetailsSource;
  name: string;
  label: string;
  phone: string;
  email: string;
  company: string;
  persona?: string;
  photoURL?: string;
  trainerProfile?: Trainer;
  loading: boolean;
  error: string | null;
}

const emptyDetails = (loading = false, error: string | null = null): TrainerDetails => ({
  source: 'none',
  name: '',
  label: '',
  phone: '',
  email: TRAINER_CONTACT_DEFAULTS.email,
  company: TRAINER_CONTACT_DEFAULTS.company,
  loading,
  error,
});

const text = (...values: unknown[]): string =>
  values.find((value) => typeof value === 'string' && value.trim().length > 0)?.toString().trim() || '';

type TrainerDetailsResponse = Omit<TrainerDetails, 'loading' | 'error'> & {
  trainerProfile?: Trainer | null;
};

export function useTrainerDetails(options: {
  trainerId?: string | null;
  aiTrainerId?: string | null;
  enabled?: boolean;
}): TrainerDetails {
  const { trainerId, aiTrainerId, enabled = true } = options;
  const [details, setDetails] = useState<TrainerDetails>(() => emptyDetails(enabled));

  useEffect(() => {
    if (!enabled) {
      setDetails(emptyDetails(false));
      return;
    }

    let cancelled = false;

    const load = async () => {
      setDetails((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const getTrainerDetails = httpsCallable<void, TrainerDetailsResponse>(
          functions,
          'getTrainerDetails'
        );
        const result = await getTrainerDetails();
        const data = result.data;
        const phone = text(data.phone) || await resolveTrainerContactPhone({
          aiTrainerId: aiTrainerId || undefined,
          fallbackPhone: import.meta.env.VITE_TWILIO_PHONE_NUMBER || '',
        });

        if (!cancelled) {
          setDetails({
            id: data.id,
            source: data.source || 'none',
            name: text(data.name),
            label: text(data.label),
            phone,
            email: text(data.email) || TRAINER_CONTACT_DEFAULTS.email,
            company: text(data.company) || TRAINER_CONTACT_DEFAULTS.company,
            persona: text(data.persona),
            photoURL: text(data.photoURL),
            trainerProfile: data.trainerProfile || undefined,
            loading: false,
            error: null,
          });
        }
      } catch (error) {
        console.warn('[useTrainerDetails] Failed to load trainer details:', error);
        if (!cancelled) {
          setDetails(emptyDetails(false, 'Unable to load trainer details.'));
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [enabled, trainerId, aiTrainerId]);

  return details;
}
